import { motion } from 'framer-motion';
import { Navbar } from '../../components/layout/Navbar';
import { Footer } from '../../components/layout/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Users, Timer, Heart, Trophy, Target } from 'lucide-react';

export default function FinRunPage() {
  const categories = [
    { name: '21K Half Marathon', icon: Trophy, description: 'Challenge yourself with the ultimate test of endurance' },
    { name: '10K Run', icon: Target, description: 'Perfect for intermediate runners looking to push limits' },
    { name: '5K Fun Run', icon: Heart, description: 'Ideal for beginners and families' },
    { name: '2K Kids Run', icon: Users, description: 'A special category for our young champions' }
  ];

  const highlights = [
    'Unique blend of fitness and financial awareness',
    'Professional chip timing and race management',
    'Finisher medals and certificates for all',
    'Expert pacing groups for all categories',
    'Hydration stations throughout the route',
    'Post-race celebrations and prizes'
  ];

  return (
    <div className="page-transition" data-testid="finrun-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1746046489457-9628dc3b8a1f?crop=entropy&cs=srgb&fm=jpg&q=85"
              alt="The FinRun Marathon"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 hero-overlay" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <span className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-gold rounded-full animate-pulse" />
                <span className="text-gold text-sm font-medium uppercase tracking-wider">
                  Live Event
                </span>
              </span>

              <h1 className="font-heading text-4xl md:text-5xl lg:text-7xl text-white mb-6">
                The <span className="text-gold">FinRun</span>
              </h1>
              
              <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-4 max-w-2xl">
                India's unique marathon that combines fitness with financial awareness. Run for health, run for wealth!
              </p>
              
              <p className="text-gold text-xl font-heading mb-8">
                "Where Every Step Counts Towards Financial Freedom"
              </p>

              <div className="flex flex-wrap gap-6 text-white/90 mb-10">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gold" />
                  <span>March 9, 2025</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-gold" />
                  <span>Hyderabad</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gold" />
                  <span>5,000+ Runners</span>
                </div>
                <div className="flex items-center gap-2">
                  <Timer className="w-5 h-5 text-gold" />
                  <span>6:00 AM Start</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                  Register Now
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold px-8 py-4 uppercase tracking-wider hover:bg-white hover:text-navy transition-colors">
                  Become a Sponsor
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                  About The Event
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy mb-6">
                  More Than Just a Marathon
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  The FinRun is a groundbreaking event that brings together running enthusiasts and finance professionals. It's not just about crossing the finish line—it's about taking the first step towards a healthier lifestyle and better financial habits.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                  Supported by leading financial institutions and fitness brands, The FinRun offers participants a unique experience that goes beyond traditional marathons.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  {highlights.map((highlight) => (
                    <div key={highlight} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-gold rounded-full mt-2" />
                      <span className="text-navy text-sm">{highlight}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <img
                  src="https://images.pexels.com/photos/2567025/pexels-photo-2567025.jpeg"
                  alt="Runners at The FinRun"
                  className="w-full h-[500px] object-cover"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Race Categories */}
        <section className="py-20 lg:py-32 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Race Categories
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy mb-6">
                Choose Your Challenge
              </h2>
              <p className="text-muted-foreground text-lg">
                Whether you're a seasoned marathoner or a first-time runner, we have a category for you.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((category, index) => (
                <motion.div
                  key={category.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 hover-lift text-center"
                  data-testid={`category-${index}`}
                >
                  <div className="w-16 h-16 bg-navy flex items-center justify-center mx-auto mb-4">
                    <category.icon className="w-8 h-8 text-gold" />
                  </div>
                  <h3 className="font-heading text-xl text-navy mb-2">{category.name}</h3>
                  <p className="text-muted-foreground text-sm">{category.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Race Day Info */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <img
                  src="https://images.unsplash.com/photo-1766066015228-9f99d6dfae5a?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Race start line"
                  className="w-full h-[400px] object-cover"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                  Race Day
                </span>
                <h2 className="font-heading text-3xl md:text-4xl text-navy mb-6">
                  What to Expect
                </h2>

                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <Timer className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Race Timing</h3>
                      <p className="text-muted-foreground">Professional chip timing with real-time tracking. Results available instantly after your finish.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <Trophy className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Prizes & Recognition</h3>
                      <p className="text-muted-foreground">Top finishers in each category receive cash prizes and trophies. All participants receive finisher medals.</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <Heart className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy mb-1">Medical Support</h3>
                      <p className="text-muted-foreground">Complete medical coverage with ambulances and first-aid stations along the route.</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 lg:py-24 bg-navy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
                Ready to Join <span className="text-gold">The FinRun?</span>
              </h2>
              <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
                Register now to secure your spot. Early bird discounts available for limited time!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                  Register Now
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold px-8 py-4 uppercase tracking-wider hover:bg-white hover:text-navy transition-colors">
                  Sponsorship Packages
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
